/**
 * GEDCOM Name Parser
 *
 * Parses GEDCOM name format: "GivenNames /Surname/ Suffix"
 * Handles various edge cases including Arabic names and missing surnames.
 *
 * Part of Phase 4 - Slice 2: Parser Integration
 */

export interface ParsedName {
  /** Given name(s) / first name(s) */
  givenName: string;

  /** Surname / family name (null if not present) */
  surname: string | null;

  /** Formatted display name for UI */
  displayName: string;

  /** Suffix (Jr., Sr., III, etc.) - optional */
  suffix?: string;
}

/**
 * GEDCOM name format regex
 * Pattern: "Given Names /Surname/ Suffix"
 * Examples:
 *   "John Robert /Doe/ Jr." → given: "John Robert", surname: "Doe", suffix: "Jr."
 *   "أحمد /سعد الدين/" → given: "أحمد", surname: "سعد الدين"
 *   "Madonna" → given: "Madonna", surname: null
 *   "/Smith/" → given: "", surname: "Smith"
 *
 * Captures:
 * 1: Given names (before first /)
 * 2: Surname (between / /)
 * 3: Suffix (after second /)
 */
const GEDCOM_NAME_REGEX = /^([^/]*)\/([^/]+)\/\s*(.+)?$/;

/**
 * Parse GEDCOM name value into structured components
 *
 * @param nameValue - Raw name value from GEDCOM NAME tag
 * @returns Parsed name object with givenName, surname, and displayName
 */
export const parseGedcomName = (nameValue: string | null): ParsedName => {
  // Handle null or empty name
  if (!nameValue || nameValue.trim() === '') {
    return {
      givenName: '',
      surname: null,
      displayName: 'Unknown',
    };
  }

  const trimmedName = nameValue.trim();

  // Try to match GEDCOM format with surname delimiters
  const match = GEDCOM_NAME_REGEX.exec(trimmedName);

  if (match) {
    // Name has surname delimiters
    const givenName = match[1]?.trim() || '';
    const surname = match[2]?.trim() || null;
    const suffix = match[3]?.trim() || undefined;

    // Build display name
    const displayName = buildDisplayName(givenName, surname, suffix);

    return {
      givenName,
      surname,
      displayName,
      suffix,
    };
  }

  // No surname delimiters - treat entire string as given name
  return {
    givenName: trimmedName,
    surname: null,
    displayName: trimmedName,
  };
};

/**
 * Build formatted display name from components
 *
 * @param givenName - Given name(s)
 * @param surname - Surname (optional)
 * @param suffix - Name suffix (optional)
 * @returns Formatted display name
 */
const buildDisplayName = (givenName: string, surname: string | null, suffix?: string): string => {
  const parts: string[] = [];

  if (givenName) {
    parts.push(givenName);
  }

  if (surname) {
    parts.push(surname);
  }

  if (suffix) {
    parts.push(suffix);
  }

  return parts.length > 0 ? parts.join(' ') : 'Unknown';
};
